import type { FC, ReactNode } from "react";
import { Breadcrumbs } from "../components/application/breadcrumbs/breadcrumbs";
import { IconChip, type IconChipColor } from "../kit/IconChip";
import { cx } from "../utils/cx";

export interface Crumb {
    label: string;
    href?: string;
}

export interface PageHeaderProps {
    title: ReactNode;
    description?: ReactNode;
    /** Buttons at the inline-end (left in RTL). */
    actions?: ReactNode;
    /** Content under the title row (tabs, filter bar). */
    children?: ReactNode;
    /** Untitled UI breadcrumbs above the title (detail pages). The last crumb is the current page. */
    breadcrumbs?: Crumb[];
    /** Section icon shown as a tinted chip before the title. */
    icon?: FC<{ className?: string }>;
    iconColor?: IconChipColor;
    /** Small label above the title (section / context). */
    eyebrow?: ReactNode;
    /** Decorative illustration at the inline-end on desktop (transparent webp). */
    illustration?: string;
    /** Subtle dotted background behind the header (default on). */
    pattern?: boolean;
    className?: string;
}

/** Dashboard page header: the only <h1> on the page, with optional icon chip, eyebrow and a soft dotted backdrop. */
export const PageHeader = ({
    title,
    description,
    actions,
    children,
    breadcrumbs,
    icon,
    iconColor = "brand",
    eyebrow,
    illustration,
    pattern = true,
    className,
}: PageHeaderProps) => (
    <div className={cx("relative isolate flex flex-col gap-5 px-4 lg:px-8", className)}>
        {pattern && (
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 -top-6 -z-10 h-44 bg-dot-grid opacity-80 [mask-image:radial-gradient(70%_100%_at_100%_0%,black_10%,transparent_75%)] lg:-top-8"
            />
        )}
        {breadcrumbs && breadcrumbs.length > 0 && (
            <Breadcrumbs type="text" divider="chevron">
                {breadcrumbs.map((c, i) => (
                    <Breadcrumbs.Item key={`${c.label}-${i}`} href={c.href}>
                        {c.label}
                    </Breadcrumbs.Item>
                ))}
            </Breadcrumbs>
        )}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex min-w-0 flex-1 items-start gap-4">
                {icon && <IconChip icon={icon} color={iconColor} size="lg" className="hidden shadow-xs sm:inline-flex" />}
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                    {eyebrow && <p className="text-sm font-semibold text-brand-secondary">{eyebrow}</p>}
                    <h1 className="text-xl font-semibold text-balance text-primary sm:text-display-xs">{title}</h1>
                    {description && <p className="max-w-3xl text-md text-pretty text-tertiary">{description}</p>}
                </div>
            </div>
            {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
            {illustration && <img src={illustration} alt="" aria-hidden="true" className="-my-4 hidden h-28 w-auto shrink-0 xl:block" />}
        </div>
        {children}
    </div>
);

/** Horizontal page gutter matching the header. */
export const PageSection = ({ children, className }: { children: ReactNode; className?: string }) => (
    <section className={cx("px-4 lg:px-8", className)}>{children}</section>
);
